import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { GetPlatformStatsAction } from "../../store/action";
import { platformStatsWorthShowing } from "../../constants/landing";

/**
 * CountUp Hook - Animates number counting
 */
const useCountUp = (end, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;

    let startTime = null;
    const startValue = 0;
    const endValue = end;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(
        easeOutQuart * (endValue - startValue) + startValue,
      );

      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration, shouldStart]);

  return count;
};

/**
 * CountBox Component - Individual counter box
 */
const CountBox = ({ icon, end, title, description, duration }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  const count = useCountUp(end, duration, isVisible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 },
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [isVisible]);

  return (
    <div ref={ref}>
      <div className="count-box flex h-full items-start gap-4 rounded-card border border-ink-200 bg-white p-6 shadow-card">
        <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
          <i className={`bi ${icon} text-xl`} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <span className="purecounter block text-3xl font-bold tabular-nums leading-none text-ink-900">
            {count.toLocaleString()}
          </span>
          <p className="mt-2 text-fluid-sm leading-snug text-ink-500">
            <strong className="font-semibold text-ink-700">{title}</strong>{" "}
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

CountBox.propTypes = {
  icon: PropTypes.string.isRequired,
  end: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  duration: PropTypes.number,
};

/**
 * Counts Component
 * Displays animated statistics/counters in a grid layout
 * Fetches real-time data from the platform statistics API
 *
 * Renders nothing until the fetched figures clear `PLATFORM_STATS.minimums` —
 * not while loading, not after a failed request. Whether it is mounted at all
 * is `PLATFORM_STATS.enabled`, decided by the caller.
 *
 * @param {Array} counters - Array of counter objects with icon, end, title, and description (optional override)
 * @param {number} animationDuration - Duration of count animation in milliseconds
 */
const Counts = ({ counters = [], animationDuration = 2000 }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);

  // Fetch platform statistics on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await GetPlatformStatsAction();
        setStats(data);
      } catch (error) {
        console.error("Error fetching platform stats:", error);
        // Leave stats empty: the section stays hidden rather than showing zeros
      }
    };

    fetchStats();
  }, []);

  const overridden = counters.length > 0;
  if (!overridden && !platformStatsWorthShowing(stats)) return null;

  // Default counters using fetched data and translations
  const countersToDisplay = overridden ? counters : [
    {
      icon: "bi-building",
      end: stats.organizationsCount,
      title: t("Counts.organizations.title"),
      description: t("Counts.organizations.description"),
    },
    {
      icon: "bi-briefcase",
      end: stats.servicesCount,
      title: t("Counts.services.title"),
      description: t("Counts.services.description"),
    },
    {
      icon: "bi-calendar-check",
      end: stats.bookingsCount,
      title: t("Counts.bookings.title"),
      description: t("Counts.bookings.description"),
    },
    {
      icon: "bi-people",
      end: stats.resourcesCount,
      title: t("Counts.resources.title"),
      description: t("Counts.resources.description"),
    },
  ];

  return (
    <section id="counts" className="counts bg-ink-50 py-14 sm:py-16">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {countersToDisplay.map((counter, index) => (
            <CountBox
              key={index}
              icon={counter.icon}
              end={counter.end}
              title={counter.title}
              description={counter.description}
              duration={animationDuration}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

Counts.propTypes = {
  counters: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      end: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
    }),
  ),
  animationDuration: PropTypes.number,
};

export default Counts;
